import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useLazyVerifyTokenQuery } from '@/services/authApi';
import {
  useLazyGetUserActiveIncidentsQuery,
  useLazyGetAllActiveIncidentsQuery,
} from '@/services/incidentsApi';
import { useLazyGetPalmettoGroupsQuery } from '@/services/palmettoGroupApi';
import { SET_ACCOUNT, SET_AUTH } from '@/store/auth/auth.action';
import { tokenVerificationSelector } from '@/store/auth/auth.selector';
import { CircularLoading } from '@zawarski/eti-ui';
import { AuthError, PortalRedirect } from '@/pages';
import { FormatCatchErrorMsg } from '@/lib/utils';

export function Auth() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const idToken = searchParams.get('id_token');
  const tokenVerification = useAppSelector(tokenVerificationSelector);
  const hasVerified = useRef(false);

  // *: RTK Query
  const [verifyToken, { isFetching, isLoading, error, data: queryData }] =
    useLazyVerifyTokenQuery();
  const [getUserActiveIncidents] = useLazyGetUserActiveIncidentsQuery();
  const [getAllIncidents] = useLazyGetAllActiveIncidentsQuery();
  const [getPalmettoGroups] = useLazyGetPalmettoGroupsQuery();

  const loading = isFetching || isLoading;
  const hasVerification = tokenVerification || queryData;

  useEffect(() => {
    // Prevent double verification (React 18+ StrictMode)
    if (!idToken || location.pathname !== '/' || hasVerified.current) return;

    hasVerified.current = true;

    verifyToken(idToken)
      .unwrap()
      .then((data) => {
        dispatch(SET_AUTH(idToken));
        dispatch(SET_ACCOUNT({ userId: data.id, verification: data }));
        // Fetch user's active incidents, user settings, and others after successful auth
        getUserActiveIncidents(undefined);
        getAllIncidents(undefined);
        getPalmettoGroups(undefined);
        navigate('/', { replace: true });
      })
      .catch((err) => {
        console.error('Token verification failed:', err);
      });
  }, [
    dispatch,
    idToken,
    location.pathname,
    navigate,
    verifyToken,
    getUserActiveIncidents,
    getAllIncidents,
    getPalmettoGroups,
  ]);

  if (error) {
    return <AuthError message={FormatCatchErrorMsg(error)} />;
  }

  // Skip token verification check for registration paths
  if (!hasVerification && !loading) {
    return <PortalRedirect />;
  }

  if (loading) {
    return (
      <div className={'bg-background flex h-screen w-screen items-center justify-center'}>
        <CircularLoading size='lg' />
      </div>
    );
  }

  return <Outlet />;
}
